// src/pages/TeamComparison.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useData } from "../context/DataContext";

// Optional MUI polish
import { Tooltip } from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

interface MatchRow {
  date: string; // ISO or parseable date
  home_team: string;
  away_team: string;
  home_score: string | number;
  away_score: string | number;
  [key: string]: any;
}

type TeamStats = {
  matches: number;
  wins: number;
  losses: number;
  draws: number;
  winPct: number;
  pointsFor: number;
  pointsAgainst: number;
  avgFor: number;
  avgAgainst: number;
  avgMargin: number;
  margins: number[]; // margin from THIS team's perspective, chronological
  dates: number[]; // ts for series sort
};

const BRAND = {
  bgPage: "#EEF5FF",
  cardBg: "#B4D4FF",
  cardBorder: "#86B6F6",
  chipBg: "#EEF5FF",
  textDark: "#0B2230",
  textMid: "rgba(11,34,48,0.72)",
  accent: "#176B87",
  white: "#FFFFFF",
};

const fmtPct = (v: number) => `${(isFinite(v) ? v : 0).toFixed(1)}%`;
const fmtNum = (v: number, d = 1) => (isFinite(v) ? v.toFixed(d) : "0.0");

const safeTs = (d: any) => {
  const t = new Date(String(d)).getTime();
  return Number.isFinite(t) ? t : NaN;
};

const toISODate = (ts: number) => {
  const d = new Date(ts);
  if (!Number.isFinite(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// ✅ Sparkline (responsive SVG that stretches to fill container)
const Spark: React.FC<{
  series: number[];
  height?: number;
  pad?: number;
  strokeWidth?: number;
}> = ({ series, height = 44, pad = 6, strokeWidth = 5 }) => {
  const W = 600; // virtual width for viewBox (scales to container)
  const H = height;

  if (!series || series.length === 0) return <div style={{ height: H }} />;

  const min = Math.min(...series);
  const max = Math.max(...series);
  const span = max - min || 1;
  const step = (W - pad * 2) / Math.max(series.length - 1, 1);

  const pts = series.map((v, i) => {
    const x = pad + i * step;
    const y = pad + (H - pad * 2) * (1 - (v - min) / span);
    return `${x},${y}`;
  });

  const last = series[series.length - 1];
  const stroke = last >= 0 ? BRAND.accent : BRAND.textDark;

  // Zero line (if it falls within range)
  const hasZero = min <= 0 && max >= 0;
  const yZero = hasZero ? pad + (H - pad * 2) * (1 - (0 - min) / span) : null;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      style={{ display: "block", width: "100%", height: H }}
      aria-hidden="true"
    >
      {hasZero && yZero !== null && (
        <line
          x1={pad}
          x2={W - pad}
          y1={yZero}
          y2={yZero}
          stroke={BRAND.cardBorder}
          strokeOpacity={0.55}
          strokeDasharray="10 10"
        />
      )}

      <polyline
        points={pts.join(" ")}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
};

const useWindowWidth = () => {
  const [w, setW] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const onResize = () => setW(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return w;
};

const Comparison: React.FC = () => {
  const w = useWindowWidth();

  // derive columns (inline styles can't be overridden by CSS media queries)
  const filterCols =
    w <= 700 ? "1fr" : w <= 1100 ? "repeat(2, 1fr)" : "repeat(4, 1fr)";
  const metricCols =
    w <= 700 ? "1fr" : w <= 1100 ? "repeat(2, 1fr)" : "repeat(4, 1fr)";
  const sparkCols = w <= 700 ? "1fr" : "1fr 1fr";

  const { rows: rawRows } = useData();

  const [teams, setTeams] = useState<string[]>([]);
  const [team1, setTeam1] = useState<string>("");
  const [team2, setTeam2] = useState<string>("");

  // Filters
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [venue, setVenue] = useState<"ALL" | "HOME" | "AWAY">("ALL"); // relative to Team 1
  const [mode, setMode] = useState<"TOTALS" | "PER_MATCH">("TOTALS");
  const [highlightLeader, setHighlightLeader] = useState<boolean>(true);

  // ✅ dataset scope toggle
  const [scope, setScope] = useState<"TOTAL_MATCHES" | "HEAD_TO_HEAD">(
    "HEAD_TO_HEAD"
  );

  // ✅ dataset min/max date (ISO yyyy-mm-dd) used to LIMIT date filters
  const [datasetMinISO, setDatasetMinISO] = useState<string>("");
  const [datasetMaxISO, setDatasetMaxISO] = useState<string>("");

  const rows = useMemo<MatchRow[]>(
    () => rawRows.filter((r) => r.home_team && r.away_team && r.date) as MatchRow[],
    [rawRows]
  );

  useEffect(() => {
    if (!rows.length) return;

    const tss = rows
      .map((r) => safeTs(r.date))
      .filter((t) => Number.isFinite(t)) as number[];

    if (tss.length) {
      const minTs = Math.min(...tss);
      const maxTs = Math.max(...tss);
      const minISO = toISODate(minTs);
      const maxISO = toISODate(maxTs);
      setDatasetMinISO(minISO);
      setDatasetMaxISO(maxISO);

      setDateFrom((prev) => {
        const p = prev?.trim();
        if (!p) return minISO;
        return p < minISO ? minISO : p > maxISO ? maxISO : p;
      });
      setDateTo((prev) => {
        const p = prev?.trim();
        if (!p) return maxISO;
        return p < minISO ? minISO : p > maxISO ? maxISO : p;
      });
    }

    const setT = new Set<string>();
    rows.forEach((r) => {
      setT.add(String(r.home_team));
      setT.add(String(r.away_team));
    });
    const list = Array.from(setT).sort((a, b) => a.localeCompare(b));
    setTeams(list);

    if (list.length >= 2 && !team1) {
      setTeam1(list[0]);
      setTeam2(list[1]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  // Enforce date ordering and clamp within dataset bounds, without loops
  useEffect(() => {
    if (!datasetMinISO || !datasetMaxISO) return;

    // Clamp to bounds
    let from = dateFrom || datasetMinISO;
    let to = dateTo || datasetMaxISO;

    if (from < datasetMinISO) from = datasetMinISO;
    if (from > datasetMaxISO) from = datasetMaxISO;
    if (to < datasetMinISO) to = datasetMinISO;
    if (to > datasetMaxISO) to = datasetMaxISO;

    // Ensure from <= to (swap if needed)
    if (from > to) {
      const tmp = from;
      from = to;
      to = tmp;
    }

    if (from !== dateFrom) setDateFrom(from);
    if (to !== dateTo) setDateTo(to);
  }, [dateFrom, dateTo, datasetMinISO, datasetMaxISO]);

  // base list depends on scope (used for HEAD_TO_HEAD and for Team1 totals)
  const filteredMatches = useMemo(() => {
    if (!team1) return [] as MatchRow[];
    if (scope === "HEAD_TO_HEAD" && !team2) return [] as MatchRow[];

    let base: MatchRow[] = [];

    if (scope === "HEAD_TO_HEAD") {
      base = rows.filter(
        (r) =>
          (r.home_team === team1 && r.away_team === team2) ||
          (r.home_team === team2 && r.away_team === team1)
      );
    } else {
      base = rows.filter((r) => r.home_team === team1 || r.away_team === team1);
    }

    if (dateFrom) {
      const s = new Date(dateFrom).getTime();
      base = base.filter((r) => {
        const t = safeTs(r.date);
        return Number.isFinite(t) && t >= s;
      });
    }
    if (dateTo) {
      const e = new Date(dateTo).getTime();
      base = base.filter((r) => {
        const t = safeTs(r.date);
        return Number.isFinite(t) && t <= e;
      });
    }

    // venue filter (relative to team1)
    if (venue === "HOME") base = base.filter((r) => r.home_team === team1);
    else if (venue === "AWAY") base = base.filter((r) => r.away_team === team1);

    return base
      .slice()
      .sort((a, b) => (safeTs(a.date) || 0) - (safeTs(b.date) || 0));
  }, [rows, team1, team2, dateFrom, dateTo, venue, scope]);

  // compute per-team match lists
  const filteredMatchesForTeam = useMemo(() => {
    return (team: string) => {
      if (!team) return [] as MatchRow[];

      if (scope === "HEAD_TO_HEAD") {
        // both teams share same match list
        return filteredMatches;
      }

      let base = rows.filter((r) => r.home_team === team || r.away_team === team);

      if (dateFrom) {
        const s = new Date(dateFrom).getTime();
        base = base.filter((r) => {
          const t = safeTs(r.date);
          return Number.isFinite(t) && t >= s;
        });
      }
      if (dateTo) {
        const e = new Date(dateTo).getTime();
        base = base.filter((r) => {
          const t = safeTs(r.date);
          return Number.isFinite(t) && t <= e;
        });
      }

      // venue filter relative to THIS team
      if (venue === "HOME") base = base.filter((r) => r.home_team === team);
      else if (venue === "AWAY") base = base.filter((r) => r.away_team === team);

      return base
        .slice()
        .sort((a, b) => (safeTs(a.date) || 0) - (safeTs(b.date) || 0));
    };
  }, [rows, scope, filteredMatches, dateFrom, dateTo, venue]);

  const computeStats = (team: string): TeamStats => {
    let matches = 0,
      wins = 0,
      losses = 0,
      draws = 0,
      pointsFor = 0,
      pointsAgainst = 0;

    const pairs: { ts: number; margin: number }[] = [];
    const list = filteredMatchesForTeam(team);

    for (const r of list) {
      const hs = Number(r.home_score);
      const as = Number(r.away_score);
      if (!Number.isFinite(hs) || !Number.isFinite(as)) continue;

      const isHome = r.home_team === team;
      const isAway = r.away_team === team;
      if (!isHome && !isAway) continue;

      matches++;

      if (hs === as) draws++;
      else {
        const won = (isHome && hs > as) || (isAway && as > hs);
        if (won) wins++;
        else losses++;
      }

      const pf = isHome ? hs : as;
      const pa = isHome ? as : hs;
      pointsFor += pf;
      pointsAgainst += pa;

      const ts = safeTs(r.date);
      if (Number.isFinite(ts)) pairs.push({ ts, margin: pf - pa });
    }

    pairs.sort((a, b) => a.ts - b.ts);

    const margins = pairs.map((p) => p.margin);
    const dates = pairs.map((p) => p.ts);

    const winPct = matches ? (wins / matches) * 100 : 0;
    const avgFor = matches ? pointsFor / matches : 0;
    const avgAgainst = matches ? pointsAgainst / matches : 0;
    const avgMargin = avgFor - avgAgainst;

    return {
      matches,
      wins,
      losses,
      draws,
      winPct,
      pointsFor,
      pointsAgainst,
      avgFor,
      avgAgainst,
      avgMargin,
      margins,
      dates,
    };
  };

  const t1 = computeStats(team1);
  const t2 = computeStats(team2);

  // Card explanations (shown via question-mark / info icon)
  const CARD_HELP: Record<string, string> = useMemo(
    () => ({
      Matches:
        scope === "HEAD_TO_HEAD"
          ? "Number of matches where Team 1 and Team 2 played each other (after filters)."
          : "Number of matches each team played in the dataset (after filters).",
      Wins: "Count of wins. A win is when the team's score > opponent score.",
      Losses: "Count of losses. A loss is when the team's score < opponent score.",
      Draws: "Count of draws (scores equal).",
      "Win %": "Win rate: Wins ÷ Matches × 100%.",
      "Points For (Total)":
        "Total points scored by the team across the filtered matches.",
      "Points For (Avg)":
        "Average points scored per match: Points For ÷ Matches.",
      "Points Against (Total)":
        "Total points conceded by the team across the filtered matches.",
      "Points Against (Avg)":
        "Average points conceded per match: Points Against ÷ Matches.",
      "Margin (Total)":
        "Total margin: (Points For − Points Against) summed across matches.",
      "Margin (Avg)":
        "Average margin per match: (Avg Points For − Avg Points Against). Positive = outscoring opponents.",
    }),
    [scope]
  );

  const METRICS: {
    label: string;
    t1: number;
    t2: number;
    fmt?: (v: number) => string;
    info?: string;
  }[] = [
    {
      label: "Matches",
      t1: t1.matches,
      t2: t2.matches,
      info:
        scope === "HEAD_TO_HEAD"
          ? "Head-to-head matches after filters"
          : "Total matches for each team after filters",
    },
    { label: "Wins", t1: t1.wins, t2: t2.wins, info: "Count of wins" },
    { label: "Losses", t1: t1.losses, t2: t2.losses, info: "Count of losses" },
    { label: "Draws", t1: t1.draws, t2: t2.draws, info: "Count of draws" },
    {
      label: "Win %",
      t1: t1.winPct,
      t2: t2.winPct,
      fmt: fmtPct,
      info: "Wins / Matches",
    },
    {
      label: mode === "TOTALS" ? "Points For (Total)" : "Points For (Avg)",
      t1: mode === "TOTALS" ? t1.pointsFor : t1.avgFor,
      t2: mode === "TOTALS" ? t2.pointsFor : t2.avgFor,
      fmt: mode === "TOTALS" ? (v) => fmtNum(v, 0) : fmtNum,
      info:
        mode === "TOTALS"
          ? "Total points scored"
          : "Average points scored per match",
    },
    {
      label:
        mode === "TOTALS"
          ? "Points Against (Total)"
          : "Points Against (Avg)",
      t1: mode === "TOTALS" ? t1.pointsAgainst : t1.avgAgainst,
      t2: mode === "TOTALS" ? t2.pointsAgainst : t2.avgAgainst,
      fmt: mode === "TOTALS" ? (v) => fmtNum(v, 0) : fmtNum,
      info:
        mode === "TOTALS"
          ? "Total points conceded"
          : "Average points conceded per match",
    },
    {
      label: mode === "TOTALS" ? "Margin (Total)" : "Margin (Avg)",
      t1:
        mode === "TOTALS"
          ? t1.pointsFor - t1.pointsAgainst
          : t1.avgMargin,
      t2:
        mode === "TOTALS"
          ? t2.pointsFor - t2.pointsAgainst
          : t2.avgMargin,
      fmt: mode === "TOTALS" ? (v) => fmtNum(v, 0) : fmtNum,
      info: "Points For − Points Against",
    },
  ];

  // Helpers
  const swapTeams = () => {
    setTeam1(team2);
    setTeam2(team1);
  };

  const resetFilters = () => {
    // keep bounds and reset to dataset range
    setVenue("ALL");
    setMode("TOTALS");
    setHighlightLeader(true);
    setScope("HEAD_TO_HEAD");

    if (datasetMinISO && datasetMaxISO) {
      setDateFrom(datasetMinISO);
      setDateTo(datasetMaxISO);
    } else {
      setDateFrom("");
      setDateTo("");
    }
  };

  const sparkHeight = 96;

  return (
    <div style={styles.page}>
      <div style={styles.headerRow}>
        <h1 style={styles.h1}>
          {scope === "HEAD_TO_HEAD"
            ? "Head-to-Head Comparison"
            : "Total Matches Comparison"}
        </h1>

        <div style={styles.headerActions}>
          <Tooltip
            title={
              scope === "HEAD_TO_HEAD"
                ? "Showing only matches where Team 1 and Team 2 played each other"
                : "Showing each team's total matches (not only head-to-head)"
            }
          >
            <button
              style={{
                ...styles.toggleBtn,
                ...(scope === "TOTAL_MATCHES" ? styles.toggleBtnActive : {}),
              }}
              onClick={() =>
                setScope((s) =>
                  s === "HEAD_TO_HEAD" ? "TOTAL_MATCHES" : "HEAD_TO_HEAD"
                )
              }
              aria-label="Toggle dataset scope"
            >
              {scope === "HEAD_TO_HEAD" ? "Head-to-head" : "Total matches"}
            </button>
          </Tooltip>

          <Tooltip title="Swap Team 1 and Team 2">
            <button
              style={styles.iconBtn}
              onClick={swapTeams}
              aria-label="Swap teams"
            >
              <SwapHorizIcon fontSize="small" />
            </button>
          </Tooltip>

          <Tooltip title="Reset filters (dates reset to dataset min/max)">
            <button
              style={styles.iconBtn}
              onClick={resetFilters}
              aria-label="Reset filters"
            >
              <RestartAltIcon fontSize="small" />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Filters Panel */}
      <div style={styles.filterCard}>
        <div style={styles.filterMetaRow}>
          <div style={styles.metaChip}>
            Dataset date range:{" "}
            <b>
              {datasetMinISO || "—"} → {datasetMaxISO || "—"}
            </b>
          </div>
          <div
            style={{
              ...styles.metaHint,
              ...(datasetMinISO ? {} : { opacity: 0.55 }),
            }}
          >
            Date inputs are clamped to this range.
          </div>
        </div>

        <div style={{ ...styles.filterGrid, gridTemplateColumns: filterCols }}>
          <div style={styles.field}>
            <label style={styles.label}>Team 1</label>
            <select
              value={team1}
              onChange={(e) => setTeam1(e.target.value)}
              style={styles.select}
            >
              {teams
                .filter((t) => t !== team2)
                .map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
            </select>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Team 2</label>
            <select
              value={team2}
              onChange={(e) => setTeam2(e.target.value)}
              style={styles.select}
            >
              {teams
                .filter((t) => t !== team1)
                .map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
            </select>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>From</label>
            <input
              type="date"
              value={dateFrom}
              min={datasetMinISO || undefined}
              max={datasetMaxISO || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
              style={styles.input}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>To</label>
            <input
              type="date"
              value={dateTo}
              min={datasetMinISO || undefined}
              max={datasetMaxISO || undefined}
              onChange={(e) => setDateTo(e.target.value)}
              style={styles.input}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Venue (vs from Team 1)</label>
            <div style={styles.segment}>
              {(["ALL", "HOME", "AWAY"] as const).map((v) => (
                <button
                  key={v}
                  onClick={() => setVenue(v)}
                  style={{
                    ...styles.segmentBtn,
                    ...(venue === v ? styles.segmentBtnActive : {}),
                  }}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Values</label>
            <div style={styles.segment}>
              {(["TOTALS", "PER_MATCH"] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  style={{
                    ...styles.segmentBtn,
                    ...(mode === m ? styles.segmentBtnActive : {}),
                  }}
                >
                  {m === "TOTALS" ? "Totals" : "Per match"}
                </button>
              ))}
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Highlight Leader</label>
            <div style={styles.segment}>
              <button
                onClick={() => setHighlightLeader((s) => !s)}
                style={{
                  ...styles.segmentBtn,
                  ...(highlightLeader ? styles.segmentBtnActive : {}),
                  minWidth: 120,
                }}
              >
                {highlightLeader ? "On" : "Off"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div style={{ ...styles.grid, gridTemplateColumns: metricCols }}>
        {METRICS.map((m) => {
          const v1 = m.fmt
            ? m.fmt(m.t1)
            : fmtNum(m.t1, Number.isInteger(m.t1) ? 0 : 1);
          const v2 = m.fmt
            ? m.fmt(m.t2)
            : fmtNum(m.t2, Number.isInteger(m.t2) ? 0 : 1);

          const t1Leads = m.t1 > m.t2;

          const expl =
            CARD_HELP[m.label] ||
            m.info ||
            "This value is computed from the filtered matches for each team.";

          return (
            <div key={m.label} style={styles.metricCard}>
              <div style={styles.metricHeader}>
                <span>{m.label}</span>

                <span style={styles.infoIcon}>
                  <Tooltip title={expl} arrow>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    >
                      <InfoOutlinedIcon fontSize="small" />
                    </span>
                  </Tooltip>
                </span>
              </div>

              <div style={styles.metricBody}>
                <div style={styles.block}>
                  <div style={styles.blockTitle}>Team 1 ({team1 || "-"})</div>
                  <div
                    style={{
                      ...styles.value,
                      ...(highlightLeader && t1Leads ? styles.valueLead : {}),
                    }}
                  >
                    {v1}
                  </div>
                </div>

                <div style={styles.sep} />

                <div style={styles.block}>
                  <div style={styles.blockTitle}>Team 2 ({team2 || "-"})</div>
                  <div
                    style={{
                      ...styles.value,
                      ...(highlightLeader && !t1Leads ? styles.valueLead : {}),
                    }}
                  >
                    {v2}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Taller margin trend cards */}
      <div style={{ ...styles.sparkRow, gridTemplateColumns: sparkCols }}>
        <div style={styles.sparkCard}>
          <div style={styles.sparkHeaderRow}>
            <div style={styles.sparkHeader}>
              Margin Trend — Team 1 ({team1 || "-"})
            </div>
            <Tooltip
              title={
                "Shows match-by-match margin over time (team points − opponent points). Above zero = positive margin."
              }
              arrow
            >
              <span style={styles.sparkInfoIcon}>
                <InfoOutlinedIcon fontSize="small" />
              </span>
            </Tooltip>
          </div>
          <Spark series={t1.margins} height={sparkHeight} strokeWidth={4} />
        </div>

        <div style={styles.sparkCard}>
          <div style={styles.sparkHeaderRow}>
            <div style={styles.sparkHeader}>
              Margin Trend — Team 2 ({team2 || "-"})
            </div>
            <Tooltip
              title={
                "Shows match-by-match margin over time (team points − opponent points). Above zero = positive margin."
              }
              arrow
            >
              <span style={styles.sparkInfoIcon}>
                <InfoOutlinedIcon fontSize="small" />
              </span>
            </Tooltip>
          </div>
          <Spark series={t2.margins} height={sparkHeight} strokeWidth={4} />
        </div>
      </div>

      <p style={styles.note}>
        Notes:{" "}
        {scope === "HEAD_TO_HEAD"
          ? "Stats are computed only from matches where these two teams faced each other after filters."
          : "Stats are computed from each team's total matches after filters (not restricted to head-to-head)."}{" "}
        Venue is relative to Team&nbsp;1 (HOME = Team 1 listed as home).
      </p>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: 20,
    background: BRAND.bgPage,
    minHeight: "100vh",
    color: BRAND.textDark,
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
    gap: 12,
    flexWrap: "wrap",
  },
  headerActions: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    flexWrap: "wrap",
  },
  iconBtn: {
    padding: 8,
    borderRadius: 10,
    border: `1px solid ${BRAND.cardBorder}`,
    background: BRAND.white,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 8px 18px rgba(23,107,135,0.10)",
  },

  toggleBtn: {
    padding: "8px 12px",
    borderRadius: 10,
    border: `1px solid ${BRAND.cardBorder}`,
    background: BRAND.white,
    cursor: "pointer",
    fontWeight: 900,
    color: BRAND.textDark,
    boxShadow: "0 8px 18px rgba(23,107,135,0.10)",
    minWidth: 140,
  },
  toggleBtnActive: {
    background: "rgba(23,107,135,0.14)",
    color: BRAND.accent,
  },

  h1: { margin: 0, fontSize: 24, fontWeight: 900, color: BRAND.accent },

  filterCard: {
    background: BRAND.cardBg,
    border: `2px solid ${BRAND.cardBorder}`,
    borderRadius: 14,
    padding: 14,
    marginBottom: 18,
    boxShadow: "0 12px 26px rgba(23,107,135,0.12)",
  },
  filterMetaRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 12,
    flexWrap: "wrap",
  },
  metaChip: {
    fontWeight: 900,
    background: BRAND.chipBg,
    padding: "8px 10px",
    borderRadius: 12,
    border: `1px solid rgba(134,182,246,0.8)`,
    color: BRAND.textDark,
  },
  metaHint: {
    color: BRAND.textMid,
    fontWeight: 700,
    fontSize: 12,
  },

  filterGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 14,
  },
  field: { display: "flex", flexDirection: "column", minWidth: 0 },
  label: {
    fontWeight: 800,
    marginBottom: 6,
    background: BRAND.chipBg,
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: 10,
    border: `1px solid rgba(134,182,246,0.8)`,
    color: BRAND.textDark,
    width: "fit-content",
  },
  select: {
    padding: "10px 12px",
    borderRadius: 12,
    border: `1px solid rgba(134,182,246,0.9)`,
    background: BRAND.white,
    fontSize: 14,
    outline: "none",
  },
  input: {
    padding: "10px 12px",
    borderRadius: 12,
    border: `1px solid rgba(134,182,246,0.9)`,
    background: BRAND.white,
    fontSize: 14,
    outline: "none",
  },
  segment: {
    display: "inline-flex",
    border: `1px solid rgba(134,182,246,0.95)`,
    borderRadius: 12,
    overflow: "hidden",
    background: BRAND.white,
    width: "fit-content",
    flexWrap: "wrap",
  },
  segmentBtn: {
    padding: "8px 12px",
    background: "transparent",
    border: "none",
    fontWeight: 800,
    cursor: "pointer",
    color: BRAND.textDark,
  },
  segmentBtnActive: {
    background: "rgba(23,107,135,0.14)",
    color: BRAND.accent,
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 18,
    marginBottom: 18,
  },
  metricCard: {
    background: BRAND.white,
    border: `1px solid rgba(134,182,246,0.9)`,
    borderRadius: 14,
    padding: 14,
    display: "flex",
    flexDirection: "column",
    minHeight: 150,
    boxShadow: "0 10px 22px rgba(23,107,135,0.10)",
  },
  metricHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontWeight: 900,
    marginBottom: 10,
    color: BRAND.accent,
    gap: 8,
  },
  infoIcon: {
    display: "inline-flex",
    alignItems: "center",
    color: BRAND.textMid,
    cursor: "help",
  },
  metricBody: {
    display: "grid",
    gridTemplateColumns: "1fr 10px 1fr",
    alignItems: "center",
    gap: 8,
    flex: 1,
  },
  block: { minWidth: 0 },
  blockTitle: {
    fontSize: 12,
    color: BRAND.textMid,
    marginBottom: 6,
    fontWeight: 700,
  },
  value: {
    fontSize: 28,
    fontWeight: 900,
    color: BRAND.textDark,
    lineHeight: 1.1,
  },
  valueLead: {
    color: BRAND.accent,
    textShadow: "0 0 0.5px rgba(0,0,0,0.15)",
  },
  sep: {
    width: 2,
    height: "70%",
    background: BRAND.cardBorder,
    opacity: 0.9,
    borderRadius: 2,
    justifySelf: "center",
  },

  sparkRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 18,
    marginBottom: 6,
  },
  sparkCard: {
    background: BRAND.white,
    borderRadius: 14,
    padding: 14,
    boxShadow: "0 10px 22px rgba(23,107,135,0.10)",
    border: `1px solid rgba(134,182,246,0.9)`,
    minHeight: 160,
    display: "flex",
    flexDirection: "column",
  },
  sparkHeaderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 8,
  },
  sparkHeader: { fontWeight: 900, color: BRAND.accent },
  sparkInfoIcon: {
    display: "inline-flex",
    alignItems: "center",
    color: BRAND.textMid,
    cursor: "help",
  },

  note: { marginTop: 10, color: BRAND.textMid },
};

export default Comparison;
